import React, { useState, useEffect } from "react";
import {
  Pagination,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";

const CustomPaginationPanel = ({
  totalCount,
  pageSizeOptions = [10, 25, 50],
  selectedPageSize,
  pageIndex,
  setSelectedPageIndex,
  setSelectedPageSize,
  onPageChange, // Callback to handle page change in the parent component
  onPageSizeChange, // Callback to handle page size change
}) => {
  const [page, setPage] = useState(pageIndex); // Ensure pageIndex is handled correctly
  const [pageSize, setPageSize] = useState(
    selectedPageSize || pageSizeOptions[0]
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  // Update page if pageIndex prop changes
  useEffect(() => {
    setPage(pageIndex);
  }, [pageIndex]);

  // Update pageSize if selectedPageSize prop changes
  useEffect(() => {
    setPageSize(selectedPageSize);
  }, [selectedPageSize]);

  const handlePageChange = (event, value) => {
    if (value >= 1 && value <= totalPages) {
      setPage(value);
      setSelectedPageIndex(value);
      if (onPageChange) {
        onPageChange(value); // Trigger callback for page change
      }
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = event.target.value;
    setPageSize(newSize);
    setSelectedPageSize(newSize);
    setSelectedPageIndex(1);
    setPage(1); // Reset to the first page after changing page size
    if (onPageSizeChange) {
      onPageSizeChange(newSize); // Trigger callback for page size change
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={"space-between"}
      sx={{ borderTop: " 1px solid", borderTopColor: "var(--ag-border-color)" }}
      px={2}
      py={1}
    >
      {/* Page size selector */}
      <Box display="flex" alignItems="center">
        <Typography>Page Size:</Typography>
        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          sx={{
            marginLeft: 1,
            minWidth: 80,
            height: 30,
            color: "var(--ag-text-color)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--ag-border-color)",
            },
            "& .MuiSelect-icon": {
              color: "var(--ag-text-color)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--ag-border-color)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--ag-border-color)", // Border color when focused
            },
          }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Pagination controls */}
      <Box display="flex" alignItems="center">
        <IconButton
          onClick={() => handlePageChange(null, 1)}
          disabled={page === 1}
          aria-label="First Page"
        >
          <FirstPage sx={{ color: "var(--ag-text-color)" }} />
        </IconButton>

        <IconButton
          onClick={() => handlePageChange(null, page - 1)}
          disabled={page === 1}
          aria-label="Previous Page"
        >
          <NavigateBefore sx={{ color: "var(--ag-text-color)" }} />
        </IconButton>

        <Typography>
          Page {page} of {totalPages}
        </Typography>

        <IconButton
          onClick={() => handlePageChange(null, page + 1)}
          disabled={page === totalPages}
          aria-label="Next Page"
        >
          <NavigateNext sx={{ color: "var(--ag-text-color)" }} />
        </IconButton>

        <IconButton
          onClick={() => handlePageChange(null, totalPages)}
          disabled={page === totalPages}
          aria-label="Last Page"
        >
          <LastPage sx={{ color: "var(--ag-text-color)" }} />
        </IconButton>
      </Box>

      {/* Row summary */}
      <Typography>
        {pageSize * (page - 1) + 1} to {Math.min(totalCount, pageSize * page)}{" "}
        of {totalCount}
      </Typography>
    </Box>
  );
};

export default CustomPaginationPanel;
